import React, { useState, useEffect, useCallback, useRef } from 'react';
import galleryData from '../data/gallery.json';

function Gallery() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const touchStartX = useRef(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const { hero, cta } = galleryData;

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');
    const apiUrl = apiBase ? `${apiBase}/api/albums` : '/api/albums';

    fetch(apiUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load albums: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const normalizedCategories = Array.isArray(data) && data.some((item) => Array.isArray(item.albums))
          ? data
          : [{ name: 'Albums', folder: 'albums', albums: Array.isArray(data) ? data : [] }];
        setCategories(normalizedCategories);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Failed to load albums');
        setLoading(false);
      });
  }, []);

  const openAlbum = (album, category) => {
    setActiveAlbum({ ...album, categoryName: category.name });
    setPhotoIndex(0);
    setModalOpen(false);
    setImageLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeAlbum = () => {
    setActiveAlbum(null);
    setModalOpen(false);
    setImageLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openModal = (index) => {
    setPhotoIndex(index);
    setModalOpen(true);
    setImageLoading(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setImageLoading(true);
  };

  const prev = useCallback(() => {
    if (!activeAlbum?.photos?.length) return;
    setPhotoIndex((i) => (i - 1 + activeAlbum.photos.length) % activeAlbum.photos.length);
    setImageLoading(true);
  }, [activeAlbum]);

  const next = useCallback(() => {
    if (!activeAlbum?.photos?.length) return;
    setPhotoIndex((i) => (i + 1) % activeAlbum.photos.length);
    setImageLoading(true);
  }, [activeAlbum]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
    setTouchEndX(null);
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current !== null) {
      setTouchEndX(e.touches?.[0]?.clientX ?? null);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX === null) {
      touchStartX.current = null;
      setTouchEndX(null);
      return;
    }

    const deltaX = touchStartX.current - touchEndX;
    const threshold = 50;
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) next();
      else prev();
    }

    touchStartX.current = null;
    setTouchEndX(null);
  };

  useEffect(() => {
    if (!activeAlbum) return;
    if (photoIndex >= activeAlbum.photos.length) {
      setPhotoIndex(activeAlbum.photos.length - 1);
    } else if (photoIndex < 0) {
      setPhotoIndex(0);
    }

    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') {
        if (modalOpen) closeModal();
        else closeAlbum();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeAlbum, modalOpen, photoIndex, prev, next]);

  const albumCount = categories.reduce((total, category) => total + (category.albums?.length || 0), 0);

  return (
    <div className="fade-in">
      <div className="hero">
        <h1>{hero.title}</h1>
        <p>{hero.subtitle}</p>
      </div>

      {activeAlbum ? (
        <div className="content-section container">
          <div className="album-viewer-header">
            <button type="button" className="album-back-btn" onClick={closeAlbum}>
              {galleryData.backLabel}
            </button>
            <div className="album-viewer-title">
              <span>{activeAlbum.categoryName}</span>
              <h2>{activeAlbum.name}</h2>
            </div>
            <span className="album-counter">{photoIndex + 1} / {activeAlbum.photos.length}</span>
          </div>

          <div className="album-viewer">
            <button type="button" className="album-arrow album-arrow--prev" onClick={prev} aria-label="Previous photo">&#8249;</button>
            <div
              className="album-viewer-img-wrap"
              onClick={() => openModal(photoIndex)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {imageLoading && <div className="image-skeleton-loader"></div>}
              <img
                src={activeAlbum.photos[photoIndex]}
                alt={`${activeAlbum.name} ${photoIndex + 1}`}
                className={`album-viewer-img ${imageLoading ? 'hidden' : ''}`}
                decoding="async"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
              <div className="album-viewer-hint">{galleryData.expandHint}</div>
            </div>
            <button type="button" className="album-arrow album-arrow--next" onClick={next} aria-label="Next photo">&#8250;</button>
          </div>

          <div className="album-thumbs">
            {activeAlbum.photos.map((src, i) => (
              <div
                key={src}
                className={`album-thumb${i === photoIndex ? ' album-thumb--active' : ''}`}
                onClick={() => { setPhotoIndex(i); setImageLoading(true); }}
              >
                <img src={src} alt="" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="content-section container">
          <h2>{galleryData.albumsTitle}</h2>
          {loading ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>{galleryData.loadingText}</p>
          ) : error ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#c00' }}>
              {error}
            </p>
          ) : albumCount === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>{galleryData.emptyText}</p>
          ) : (
            <div className="gallery-category-list">
              {categories.map((category) => (
                <section key={category.folder || category.name} className="gallery-category">
                  <div className="gallery-category-header">
                    <h3>{category.name}</h3>
                    <span>{category.albums?.length || 0} album{category.albums?.length === 1 ? '' : 's'}</span>
                  </div>

                  {category.albums?.length ? (
                    <div className="album-grid">
                      {category.albums.map((album) => (
                        <div key={album.folder} className="album-card" onClick={() => openAlbum(album, category)}>
                          <div className="album-card-img-wrap">
                            <img src={album.cover} alt={album.name} loading="lazy" />
                            <div className="album-card-overlay"><span>{galleryData.openAlbumLabel}</span></div>
                          </div>
                          <div className="album-card-label">{album.name}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="gallery-category-empty">{galleryData.emptyCategoryText}</p>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      {modalOpen && activeAlbum && (
        <div className="gallery-modal" onClick={closeModal}>
          <div
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <button type="button" className="gallery-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
            <button type="button" className="modal-arrow modal-arrow--prev" onClick={prev} aria-label="Previous">&#8249;</button>
            <div className="gallery-modal-image-wrapper">
              {imageLoading && <div className="image-skeleton-loader"></div>}
              <img
                src={activeAlbum.photos[photoIndex]}
                alt={`${activeAlbum.name} ${photoIndex + 1}`}
                className={`${imageLoading ? 'hidden' : ''}`}
                decoding="async"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
            </div>
            <button type="button" className="modal-arrow modal-arrow--next" onClick={next} aria-label="Next">&#8250;</button>
            <p className="gallery-modal-caption">{photoIndex + 1} / {activeAlbum.photos.length}</p>
          </div>
        </div>
      )}

      <div className="content-section container call-to-action-section">
        <h2 style={{ color: '#FF9933' }}>{cta.title}</h2>
        <p style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>{cta.text}</p>
        {cta.links.map((link, index) => (
          <a
            key={link.to}
            href={link.to}
            className={link.className}
            style={index === 0 ? { marginRight: '1rem' } : undefined}
          >
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
}

export default Gallery;
